import { ButtonWithDialog, Intent } from "@focus/focusUI";
import { useMemo, useState } from "react";
import { ROLES, useRoles } from "../../hooks/useRoles";
import { useUserController } from "../../hooks/useUserController";
import type { User } from "../../types/users";
import { PasswordForm } from "./PasswordForm";
import type { ExtendedUser } from "./UserForm";

interface ResetPasswordButtonProps {
  user?: User;
  text?: string;
  intent?: Intent;
  disabled?: boolean;
}

export const ResetPasswordButton = ({
  user,
  text,
  intent,
  disabled,
}: ResetPasswordButtonProps) => {
  const { organizations, resetPassword } = useUserController();
  const { roles } = useRoles();
  const [acceptDisabled, setAcceptDisabled] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (data: ExtendedUser) => {
    const newErrors: Record<string, string> = {};

    // Validación de password
    if (!data.password) {
      newErrors.password = "Password is required";
    } else {
      if (data.password.length < 10) {
        newErrors.password = "Password must be at least 10 characters";
      } else if (!/[A-Z]/.test(data.password)) {
        newErrors.password =
          "Password must include at least one uppercase letter";
      } else if (!/[a-z]/.test(data.password)) {
        newErrors.password =
          "Password must include at least one lowercase letter";
      } else if (!/[!@#$%^&*()_+~`|}{[\]:;?><,./\-=/]/.test(data.password)) {
        newErrors.password = "Password must include at least one symbol";
      }

      // Validación de confirmPassword
      if (!data.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (data.password !== data.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    return newErrors;
  };

  const canEdit = useMemo(() => {
    if (roles.includes(ROLES.ADMIN)) return true;
    if (!roles.includes(ROLES.ORG_ADMIN)) return false;

    return (
      user?.organizations?.some((org) => organizations.includes(org)) ?? false
    );
  }, [roles, organizations, user?.organizations]);

  if (!canEdit) return null;

  return (
    <ButtonWithDialog<ExtendedUser>
      text={text}
      intent={intent}
      minimal={!intent}
      icon="refresh"
      headerTitle={`Reset ${user?.username} password`}
      defaultContent={{ ...user, password: "", confirmPassword: "" }}
      onAccept={(updated) => resetPassword(updated.id, updated.password)}
      disableAccept={!canEdit || acceptDisabled}
      tooltipText={text ? undefined : "Reset password"}
      form={({ content, onUpdate }) => (
        <PasswordForm
          content={content}
          onChange={(patch) => {
            const updatedContent = { ...content, ...patch };
            const validationErrors = validate(updatedContent);

            setErrors(validationErrors);
            setAcceptDisabled(Object.keys(validationErrors).length > 0);
            onUpdate(updatedContent);
          }}
          errors={errors}
        />
      )}
      disabled={disabled}
    />
  );
};
